import { FileContentSchema, FileListingSchema } from './files.js';
import {
  VelocityPathSchema,
  VelocityWriteSchema,
  VelocityUpdateSchema,
  VelocityPluginFileSchema,
  VelocityStatusSchema,
  FloodgateKeySchema,
} from './velocity.js';
import { BedrockSettingsSchema } from './bedrock.js';
import { UpdateAgentFields } from './version.js';
// edge VM の設定生成（SPEC 2.4・2.5）: Velocity・mc-router・Caddy。
import { createHash } from 'node:crypto';
import { z } from 'zod';
import { ForwardSchema, type Forward } from './ports.js';
import { generateBansNft } from './network-bans.js';
import { maintenanceCaddyBlock } from './maintenance-page.js';

export interface EdgeServer {
  id: string;
  name: string;
  proxied: boolean;
  maxPlayers?: number;
  /** 公開用ネットワーク上の IP（agent が報告し、Panel が CIDR を検査したもの） */
  address?: string;
  running: boolean;
}

export interface EdgeInput {
  domain: string;
  velocityBedrockPort?: number;
  forwards?: Forward[];
  /** Panel（Web・MCP）の転送先。例: 10.10.0.5:8080 */
  panelUpstream: string;
  homepage?: boolean;
  panelHost?: string;
  mcpHost?: string;
  /** 既存 Web などの追加サイト */
  extraSites: Array<{ host: string; upstream: string }>;
  servers: EdgeServer[];
  /** hub にするサーバー ID（Velocity の try の先頭） */
  hubId?: string;
  velocityBind: string;
  /** mc-router → Velocity / 直結サーバーで PROXY protocol を使うか */
  proxyProtocol: boolean;
  /** 全サーバー共通の IP BAN（検証済みのアドレス・範囲） */
  ipBans?: string[];
}

export const RouteEntrySchema = z.object({
  hostname: z.string(),
  kind: z.enum(['velocity', 'direct', 'panel', 'mcp', 'web']),
  target: z.string(),
  serverId: z.string().optional(),
});
export type RouteEntry = z.infer<typeof RouteEntrySchema>;

export interface EdgeConfig {
  files: EdgeFiles;
  /** Velocity で最初に入るサーバー（try の先頭） */
  hub?: string;
  routes: RouteEntry[];
  /** Velocity の [servers] が変わったか判定するための値 */
  velocityServers: Record<string, string>;
  backendAddresses: Record<string, string>;
  warnings: string[];
  hash: string;
}

const HOST = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const UPSTREAM = /^[a-zA-Z0-9.-]+:[0-9]{1,5}$/;

function tomlString(value: string): string {
  return JSON.stringify(value);
}

/** hub にする Velocity 配下のサーバー。指定がなければ id が hub のもの、なければ最初のもの */
export function pickHub(proxiedIds: string[], hubId?: string): string | undefined {
  if (hubId && proxiedIds.includes(hubId)) return hubId;
  return proxiedIds.includes('hub') ? 'hub' : [...proxiedIds].sort((a, b) => a.localeCompare(b))[0];
}
/** プレイヤーがそのサーバーに入るときのホスト名。hub はドメインそのもの、ほかは <id>.<domain>（Velocity 配下ならまず hub に入る） */
export function playerHost(server: { id: string; proxied: boolean }, domain: string, hub: string | undefined) {
  return server.proxied && server.id === hub ? domain : `${server.id}.${domain}`;
}

export function generateEdgeConfig(input: EdgeInput): EdgeConfig {
  if (!HOST.test(input.domain)) throw new Error(`Invalid domain: ${input.domain}`);
  if (!UPSTREAM.test(input.panelUpstream)) throw new Error('Invalid panel upstream');
  if (!UPSTREAM.test(input.velocityBind)) throw new Error('Invalid Velocity bind address');
  const warnings: string[] = [];
  const routes: RouteEntry[] = [];
  const panelHost = input.panelHost ?? `panel.${input.domain}`;
  const mcpHost = input.mcpHost ?? `mcp.${input.domain}`;
  const playHost = `play.${input.domain}`;

  // Velocity 配下のサーバー
  const proxied = input.servers.filter((s) => s.proxied).sort((a, b) => a.id.localeCompare(b.id));
  const velocityServers: Record<string, string> = {};
  for (const server of proxied) {
    if (!server.address) {
      warnings.push(`${server.name}: IP アドレスが分からないため Velocity に登録できません（agent が未接続）`);
      continue;
    }
    velocityServers[server.id] = `${server.address}:25565`;
  }
  const hub = pickHub(Object.keys(velocityServers), input.hubId);
  if (proxied.length && !hub) warnings.push('Velocity の最初の接続先（hub）がありません');

  // 直結サーバー（Mod 入りなど）
  const mappings: Record<string, string> = {};
  if (Object.keys(velocityServers).length) {
    mappings[playHost] = input.velocityBind;
    // ドメインそのもの（例: mc-shouchan.jp）でも hub に入れるようにする。
    mappings[input.domain] = input.velocityBind;
    for (const hostname of [input.domain, playHost])
      routes.push({
        hostname,
        kind: 'velocity',
        target: hub ? `Velocity → 最初は ${hub}` : 'Velocity（最初に入るサーバーなし）',
      });
    for (const id of Object.keys(velocityServers))
      routes.push({
        hostname: `${playHost}（/server ${id}）`,
        kind: 'velocity',
        target: velocityServers[id]!,
        serverId: id,
      });
    // サーバーごとのホスト名も Velocity に入れる。forced-hosts は使わず、どのホスト名でもまず hub に入り、
    // hub のポータルや /server で移る（Mod なしのサーバーは必ず hub を通す）
    for (const id of Object.keys(velocityServers)) {
      if (id === hub) continue;
      const host = `${id}.${input.domain}`;
      mappings[host] = input.velocityBind;
      routes.push({
        hostname: host,
        kind: 'velocity',
        target: hub ? `Velocity → 最初は ${hub}（/server ${id} で移動）` : `Velocity（/server ${id}）`,
        serverId: id,
      });
    }
  }
  for (const server of input.servers.filter((s) => !s.proxied).sort((a, b) => a.id.localeCompare(b.id))) {
    const host = `${server.id}.${input.domain}`;
    if (!server.address) {
      warnings.push(`${server.name}: IP アドレスが分からないため ${host} を登録できません（agent が未接続）`);
      continue;
    }
    mappings[host] = `${server.address}:25565`;
    routes.push({ hostname: host, kind: 'direct', target: mappings[host]!, serverId: server.id });
  }

  if (input.velocityBedrockPort)
    routes.push({
      hostname: `${input.domain}:${input.velocityBedrockPort} (UDP)`,
      kind: 'velocity',
      target: '統合版 → Velocity（Geyser）',
    });

  // Caddy
  const sites = [
    { host: panelHost, upstream: input.panelUpstream, kind: 'panel' as const },
    { host: mcpHost, upstream: input.panelUpstream, kind: 'mcp' as const },
    ...(input.homepage !== false ? [{ host: input.domain, upstream: input.panelUpstream, kind: 'web' as const }] : []),
    ...input.extraSites
      .filter((s) => input.homepage === false || ![input.domain, `www.${input.domain}`].includes(s.host))
      .map((s) => ({ ...s, kind: 'web' as const })),
  ];
  for (const site of sites) {
    if (!HOST.test(site.host) || !UPSTREAM.test(site.upstream)) throw new Error(`Invalid site: ${site.host}`);
    routes.push({ hostname: site.host, kind: site.kind, target: site.upstream });
  }
  const homepageRedirect =
    input.homepage !== false ? `\nwww.${input.domain} {\n\tredir https://${input.domain}{uri} permanent\n}\n` : '';
  if (input.homepage !== false)
    routes.push({ hostname: `www.${input.domain}`, kind: 'web', target: `https://${input.domain}` });
  const caddyfile = `# mc-panel が生成（手で編集しない）\n${sites
    .map(
      (s) =>
        `${s.host} {\n\tencode zstd gzip\n\treverse_proxy ${s.upstream} {\n\t\theader_up X-Real-IP {remote_host}\n\t}\n${
          // Panel に届かない間（更新・再起動）は、空のエラーではなくメンテナンス中の画面を出す
          s.upstream === input.panelUpstream ? maintenanceCaddyBlock(input.domain, s.kind === 'mcp') : ''
        }}\n`,
    )
    .join('\n')}${homepageRedirect}`;

  const velocity = [
    '# mc-panel が生成（手で編集しない）',
    'config-version = "2.7"',
    `bind = ${tomlString(input.velocityBind)}`,
    `motd = ${tomlString(`<green>${input.domain}`)}`,
    `show-max-players = ${proxied.reduce((sum, server) => sum + (server.maxPlayers ?? 20), 0)}`,
    'online-mode = true',
    'force-key-authentication = true',
    'prevent-client-proxy-connections = false',
    'player-info-forwarding-mode = "modern"',
    'forwarding-secret-file = "forwarding.secret"',
    'announce-forge = false',
    'kick-existing-players = false',
    'ping-passthrough = "DISABLED"',
    'enable-player-address-logging = true',
    '',
    '[servers]',
    ...Object.entries(velocityServers).map(([id, address]) => `${id} = ${tomlString(address)}`),
    `try = [${hub ? tomlString(hub) : ''}]`,
    '',
    '[forced-hosts]',
    '',
    '[advanced]',
    'compression-threshold = 256',
    'compression-level = -1',
    'login-ratelimit = 3000',
    'connection-timeout = 5000',
    'read-timeout = 30000',
    `haproxy-protocol = ${input.proxyProtocol}`,
    'tcp-fast-open = false',
    'bungee-plugin-message-channel = true',
    'show-ping-requests = false',
    'failover-on-unexpected-server-disconnect = true',
    'announce-proxy-commands = true',
    'log-command-executions = false',
    'log-player-connections = true',
    'accepts-transfers = false',
    '',
    '[query]',
    'enabled = false',
    'port = 25577',
    'map = "Velocity"',
    'show-plugins = false',
    '',
  ].join('\n');

  // ホスト名で振り分けられない接続（LAN から IP で直接つないだ場合など）も hub に通す。
  const defaultServer = Object.keys(velocityServers).length ? input.velocityBind : null;
  const routesJson = `${JSON.stringify({ 'default-server': defaultServer, mappings }, null, 2)}\n`;
  const files = {
    'velocity.toml': velocity,
    'routes.json': routesJson,
    Caddyfile: caddyfile,
    'forwards.nft': generateForwardsNft(input.forwards ?? [], input.velocityBedrockPort, input.ipBans ?? []),
  };
  const hash = createHash('sha256').update(JSON.stringify(files)).digest('hex');
  const backendAddresses = Object.fromEntries(input.servers.filter((s) => s.address).map((s) => [s.id, s.address!]));
  return { files, hub, routes, velocityServers, backendAddresses, warnings, hash };
}

export function generateForwardsNft(entries: Forward[], velocityBedrockPort?: number, ipBans: string[] = []): string {
  const ports = new Set<number>();
  for (const entry of entries) {
    ForwardSchema.parse(entry);
    if (ports.has(entry.publicPort)) throw new Error('Duplicate public port');
    ports.add(entry.publicPort);
  }
  if (velocityBedrockPort !== undefined) {
    BedrockSettingsSchema.shape.port.parse(velocityBedrockPort);
    if (ports.has(velocityBedrockPort)) throw new Error('Duplicate public port');
  }
  const sorted = [...entries].sort((a, b) => a.publicPort - b.publicPort);
  return [
    '# mc-panel が生成（手で編集しない）',
    'add table inet mc_bedrock',
    'delete table inet mc_bedrock',
    'add table inet mc_forwards',
    'delete table inet mc_forwards',
    'table inet mc_forwards {',
    ...(velocityBedrockPort
      ? [
          '  chain input {',
          '    type filter hook input priority filter; policy accept;',
          `    udp dport ${velocityBedrockPort} accept`,
          '  }',
        ]
      : []),
    '  chain prerouting {',
    '    type nat hook prerouting priority dstnat; policy accept;',
    ...sorted.map((e) => `    ${e.protocol} dport ${e.publicPort} dnat ip to ${e.address}:${e.internalPort}`),
    '  }',
    '  chain postrouting {',
    '    type nat hook postrouting priority srcnat; policy accept;',
    ...sorted.map((e) => `    ip daddr ${e.address} ${e.protocol} dport ${e.internalPort} ct status dnat masquerade`),
    '  }',
    '}',
    generateBansNft(ipBans),
    '',
  ].join('\n');
}

/** IPv4 アドレスが CIDR に入っているか（公開用ネットワーク以外の IP を報告されても使わない） */
export function ipv4InCidr(address: string, cidr: string): boolean {
  const toInt = (ip: string) => {
    const parts = ip.split('.').map(Number);
    if (parts.length !== 4 || parts.some((p) => !Number.isInteger(p) || p < 0 || p > 255)) return undefined;
    return ((parts[0]! << 24) >>> 0) + (parts[1]! << 16) + (parts[2]! << 8) + parts[3]!;
  };
  const [base, bitsText] = cidr.split('/');
  const bits = Number(bitsText);
  const ip = toInt(address);
  const network = toInt(base ?? '');
  if (ip === undefined || network === undefined || !Number.isInteger(bits) || bits < 0 || bits > 32) return false;
  const mask = bits === 0 ? 0 : (~0 << (32 - bits)) >>> 0;
  return (ip & mask) >>> 0 === (network & mask) >>> 0;
}

// ---- edge の agent と Panel の通信 ----
export const EdgeFilesSchema = z
  .object({
    'velocity.toml': z.string().max(200_000),
    'routes.json': z.string().max(200_000),
    Caddyfile: z.string().max(200_000),
    'forwards.nft': z.string().max(200_000),
  })
  .strict();
export type EdgeFiles = z.infer<typeof EdgeFilesSchema>;
export const EdgeHashSchema = z.string().regex(/^[a-f0-9]{64}$/);
export const EdgeAppliedStateSchema = z.object({ hash: EdgeHashSchema, appliedAt: z.string().datetime() }).strict();
export const EdgePanelMessageSchema = z.discriminatedUnion('type', [
  z.object({ id: z.string().uuid(), type: z.literal('velocity_start') }).strict(),
  z.object({ id: z.string().uuid(), type: z.literal('velocity_stop') }).strict(),
  VelocityUpdateSchema.extend({ id: z.string().uuid(), type: z.literal('velocity_update') }).strict(),
  z.object({ id: z.string().uuid(), type: z.literal('velocity_files_list'), path: VelocityPathSchema }).strict(),
  z.object({ id: z.string().uuid(), type: z.literal('velocity_files_read'), path: VelocityPathSchema }).strict(),
  z.object({ id: z.string().uuid(), type: z.literal('velocity_files_mkdir'), path: VelocityPathSchema }).strict(),
  z.object({ id: z.string().uuid(), type: z.literal('velocity_files_trash'), path: VelocityPathSchema }).strict(),
  VelocityWriteSchema.extend({ id: z.string().uuid(), type: z.literal('velocity_files_write') }).strict(),
  z
    .object({
      id: z.string().uuid(),
      type: z.literal('velocity_files_upload'),
      path: VelocityPathSchema,
      uploadId: z.string().uuid(),
      offset: z.number().int().min(0),
      data: z.string().max(700_000),
      final: z.boolean(),
      overwrite: z.boolean(),
    })
    .strict(),
  z.object({ id: z.string().uuid(), type: z.literal('velocity_status') }).strict(),
  z.object({ id: z.string().uuid(), type: z.literal('velocity_restart') }).strict(),
  z
    .object({ id: z.string().uuid(), type: z.literal('velocity_logs'), lines: z.number().int().min(1).max(1000) })
    .strict(),
  z.object({ id: z.string().uuid(), type: z.literal('velocity_key') }).strict(),
  z
    .object({
      id: z.string().uuid(),
      type: z.literal('velocity_plugins'),
      files: z.array(VelocityPluginFileSchema).max(200),
      bedrock: BedrockSettingsSchema,
    })
    .strict(),
  z.object({ id: z.string().uuid(), type: z.literal('update_agent'), ...UpdateAgentFields }).strict(),
  z
    .object({
      type: z.literal('apply_edge'),
      id: z.string().uuid(),
      files: EdgeFilesSchema,
      hash: EdgeHashSchema,
      restartVelocity: z.boolean(),
    })
    .strict(),
]);
export type EdgePanelMessage = z.infer<typeof EdgePanelMessageSchema>;
export const EdgeAgentMessageSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('hello'),
      role: z.literal('edge'),
      capabilities: z.array(z.string()).max(20).optional(),
      version: z.string().max(50),
      hash: EdgeHashSchema.optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('result'),
      id: z.string().uuid(),
      ok: z.boolean(),
      data: z
        .object({
          status: VelocityStatusSchema.optional(),
          listing: FileListingSchema.optional(),
          file: FileContentSchema.optional(),
          sha256: z.string().optional(),
          trashedTo: z.string().optional(),
          lines: z.array(z.string().max(10000)).max(1000).optional(),
          key: FloodgateKeySchema.optional(),
          // velocity_files_upload の進み具合
          received: z.number().int().min(0).optional(),
          done: z.boolean().optional(),
        })
        .strict()
        .optional(),
      error: z.string().max(2000).optional(),
    })
    .strict(),
]);
export type EdgeAgentMessage = z.infer<typeof EdgeAgentMessageSchema>;

export const RoutingStatusSchema = z.object({
  domain: z.string(),
  /** Velocity で最初に入るサーバー。Velocity 配下のサーバーがなければない */
  hub: z.string().optional(),
  entries: z.array(RouteEntrySchema),
  edge: z.object({
    state: z.enum(['applied', 'pending', 'error', 'unknown']),
    appliedAt: z.string().optional(),
    message: z.string().optional(),
    ipChanged: z.boolean().optional(),
    changedServers: z.array(z.string()).optional(),
  }),
});
export type RoutingStatus = z.infer<typeof RoutingStatusSchema>;
